import React, { FC, Fragment } from 'react';
import { Box, Grid } from '@material-ui/core';
import { withStyles } from '@material-ui/styles';
import E5LinkButton from 'components/retail/atoms/E5LinkButton';
import { retailResultBg } from 'assets/images/retail';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import { ENERGY_RATES_ROUTE } from 'config/retail/routes';
import { CompareRatesResult } from 'containers/retail/sections/compare-energy-rates/CompareRatesForm2';
import useI18n from "evcharging/hooks/useI18n";

const Wrapper = withStyles((theme) => ({
  root: {
    marginTop: 90,
    [theme.breakpoints.up('sm')]: {
      marginTop: 162,
    },
  },
}))(Box);

const Container = withStyles(() => ({
  root: {},
}))(Grid);

const ResultBox = withStyles(() => ({
  root: {
    background: '#FFFFFF',
    border: '1px solid #D3D5DC',
    boxShadow: '0px 11px 23px rgba(0, 0, 0, 0.12)',
    borderRadius: 6,
    backgroundImage: `url(${retailResultBg})`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 0 bottom 0',
    padding: 32,
  },
}))(Box);

const ResultWrapper = withStyles(() => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    maxWidth: 390,
    marginTop: 16,
  },
}))(Box);

const StepNumber = withStyles(() => ({
  root: {
    minWidth: 73,
    height: 73,
    background: 'rgba(249, 193, 6, 0.25)',
    border: '1px solid #F9C106',
    borderRadius: 6,
    fontWeight: 700,
    fontSize: 36,
    lineHeight: '120%',
    color: '#F9C106',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
}))(Box);

const StepText = withStyles((theme) => ({
  root: {
    marginLeft: 16,
    fontWeight: 700,
    fontSize: 17,
    lineHeight: '150%',
    color: '#333333',
    [theme.breakpoints.up('md')]: {
      fontSize: 24,
      marginLeft: 32,
    },
  },
}))(Box);

const StepBox = withStyles(() => ({
  root: {
    display: 'flex',
    alignItems: 'center',
  },
}))(Box);

const LineBox = withStyles(() => ({
  root: {
    marginTop: 12,
    marginBottom: 12,
    width: 73,
    display: 'flex',
    justifyContent: 'center',
  },
}))(Box);

const useLeftStyles = makeStyles(() =>
  createStyles({
    headerWrapper: {
      display: 'flex',
      justifyContent: 'space-between',
    },
    header: {
      fontWeight: 700,
      fontSize: 24,
      lineHeight: '120%',
      color: '#333333',
      whiteSpace: 'pre-line'
    },
    priceLabel: {
      fontWeight: 400,
      fontSize: 18,
      lineHeight: '150%',
      color: '#F9C106',
    },
    price: {
      fontWeight: 700,
      fontSize: 48,
      lineHeight: '120%',
      color: '#000',
    },
    perUnit: {
      fontWeight: 600,
      fontSize: 16,
      lineHeight: '20px',
      color: '#000',
    },
  })
);

const useRightStyles = makeStyles(() =>
  createStyles({
    header: {
      fontWeight: 700,
      fontSize: 32,
      lineHeight: '130%',
      color: '#333333',
      marginBottom: 48,
    },
  })
);

const steps = [
  'compare-rates.next-steps-text1',
  'compare-rates.next-steps-text2',
  'compare-rates.next-steps-text3',
];

const RetailResultSection: FC<CompareRatesResult> = (props) => {
  const { plan, wasFound, retailLocation } = props;
  const { t } = useI18n();
  const leftClasses = useLeftStyles();
  const rightClasses = useRightStyles();

  return (
    <Wrapper>
      <Container container>
        <Grid item xs={12} md={6}>
          <ResultWrapper>
            <ResultBox>
              <Box className={leftClasses.headerWrapper}>
                <h1 className={leftClasses.header}>
                  {t('compare-rates.lowest-rate')}
                </h1>
                <Box>
                  <img
                    src={
                      'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAB8AAAAkCAYAAABxE+FXAAAACXBIWXMAAAsTAAALEwEAmpwYAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAIrSURBVHgBxZfbddpAEIZnFt14UwmUQBqwpApSgkkH6QDoIB1Y7sCpQMINxKkguIL4wedYF7zjWSHZRrbuSP45R4hh2e/XIGYGhFxPgeWKmbyEHiKiv5aT/oKO0tQh3hkbALkGgl5CfsQ7zTadw6bT5/iKF0LIf3AWyW0XA5oAuTyJEPjQUihoQYTuW0SsOQPQ1oAmgWzBaStkuskPaKmnQF8JAe5ptL0BAaMoM7BpXAWjqdnAiPBmAyPD6w1MAK82MBE8N3A7+34SgQmFJOz3rzUYoLmX+vzk163h0l1ZtCe9ckL5AF8BRyLfvHj+DVPDFdhw0w9le3R4FVip9oaLQ+OKe84Kuoi74vvmVAVWmvSGewz0JQVgTw5X05Iu8E8qjKAwUJt2SfJ6hhhCBz0T3ZdjUWD+5O9jrc75R788Gki8WvjcO4QwUGpMMyHyUzAuFbgwEKNxNWra453u83wYRGDZukw8npfuivcQ5Z3W4NrlRC6qV8z2cy8Kq8CMyEbxzIC0PEtGXpZykDc8Zm1r4QJ5jkdcVS4g6fMxrAPnWhQG5k707XV/GCKEfQvwm4HSpNy7q+WVa9sSzD5pZXjpTWt4XqlajdKNYCe9LsfPcrf3AZ8F3hes9CHt8S03k882IgrLGw0BH/fkOpsI4z80qdSthoKVBHqgRpsNdNYwsFKWdtNJtvyn714gOpUopBCa7HQAH9f3VHkq7QpW6l9kGPZ6jrTXLw476KgXyDEGnUjtP6kAAAAASUVORK5CYII='
                    }
                    alt={'result'}
                  />
                </Box>
              </Box>
              <Box mb={13} />
              <p className={leftClasses.priceLabel}>
                {t('compare-rates.best-price')}
              </p>
              <p className={leftClasses.price}>
                ¢{plan?.price?.amountMinor}
                <span className={leftClasses.perUnit}>/ {t('compare-rates.per-kWh')}</span>
              </p>
            </ResultBox>
            <Box mb={6} />
            {wasFound && (
              <E5LinkButton href={ENERGY_RATES_ROUTE({ slug: retailLocation.slug })}>
                {t('compare-rates')}
                <img
                  style={{ marginLeft: 12, marginTop: 2 }}
                  src={
                    'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABYAAAAMCAYAAABm+U3GAAAACXBIWXMAAAsTAAALEwEAmpwYAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAABSSURBVHgB3dPBDQAQDIXhF3G3gs3YhM2sZINqo1cn78KXCKdf0qTABRHJegrYNFpl62DTaPsjHvUxwGUfzACu6XcCi43AR9HA8lS00qMePq70Agg/bYkZvAm7AAAAAElFTkSuQmCC'
                  }
                  alt={'arrow'}
                />
              </E5LinkButton>
            )}
            <Box mb={6} />
          </ResultWrapper>
        </Grid>
        <Grid item xs={12} md={6}>
          <h2 className={rightClasses.header}>{t('compare-rates.next-steps')}</h2>
          {steps.map((text, index) => (
            <Fragment key={index}>
              <StepBox>
                <StepNumber>{index + 1}</StepNumber>
                <StepText>{t(text)}</StepText>
              </StepBox>
              {index < steps.length - 1 && (
                <LineBox>
                  <img
                    src={
                      'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAIAAAAqCAYAAACZSYp/AAAACXBIWXMAAAsTAAALEwEAmpwYAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAB7SURBVHgBhZDtCYAgEIbvDpPGcBRHaZPapDaxXKS2iPrRZVqQH9GB8IKP+rziNkmG9+BqanUFQXTMPjDjmDJV48NzXADDEDNuq40ZBG4Sxso+ZghJZ/cEDbcUFCfSWFJVoe86ZD5UC3UAuj/VvM5uq/CE+zHtgxSkyqonvhI6/GLm+ywAAAAASUVORK5CYII='
                    }
                    alt={'line'}
                  />
                </LineBox>
              )}
            </Fragment>
          ))}
        </Grid>
      </Container>
    </Wrapper>
  );
};

export default RetailResultSection;
